import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { 
    CAMERA_CONFIG, 
    SCENE_CONFIG, 
    CONTROLS_CONFIG, 
    ZOOM_LEVELS, 
    DEFAULT_ZOOM_LEVEL 
} from '../utils/Constants.js';

/**
 * Manages the Three.js scene, camera, renderer, and controls
 */
export class SceneManager {
    constructor(container) {
        this.container = container;
        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.controls = null;
        this.currentZoomLevel = DEFAULT_ZOOM_LEVEL;
        this.animationId = null;
        this.onZoomChange = null; // Callback for zoom changes
        
        this.initialize();
    }

    /**
     * Initialize the 3D scene
     */
    initialize() {
        // Create scene
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(SCENE_CONFIG.BACKGROUND_COLOR);

        // Create camera
        this.camera = new THREE.PerspectiveCamera(
            CAMERA_CONFIG.DEFAULT_FOV,
            this.container.clientWidth / this.container.clientHeight,
            CAMERA_CONFIG.NEAR_PLANE,
            CAMERA_CONFIG.FAR_PLANE
        );
        this.camera.position.set(
            CAMERA_CONFIG.POSITION.x,
            CAMERA_CONFIG.POSITION.y,
            CAMERA_CONFIG.POSITION.z
        );

        // Create renderer
        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            powerPreference: 'high-performance' // Force dedicated GPU if available
        });
        this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;
        
        // Configure canvas
        this.renderer.domElement.style.cursor = 'grab';
        this.renderer.domElement.style.touchAction = 'none';
        this.renderer.domElement.style.pointerEvents = 'auto';
        this.container.appendChild(this.renderer.domElement);

        // Create controls
        this.setupControls();
        
        // Setup zoom handling
        this.setupZoomControls();
        
        // Setup mouse interaction
        this.setupMouseInteraction();

        console.log('✅ Scene initialized');
    }

    /**
     * Setup OrbitControls
     */
    setupControls() {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = CONTROLS_CONFIG.DAMPING_FACTOR;
        this.controls.enableZoom = true;
        this.controls.enableRotate = true;
        this.controls.enablePan = false;
        this.controls.rotateSpeed = CONTROLS_CONFIG.ROTATE_SPEED;
        this.controls.zoomSpeed = CONTROLS_CONFIG.ZOOM_SPEED;
        this.controls.target.set(0, 0, 0);
        this.controls.enableKeys = false;
        this.controls.enabled = true;
        
        // Configure mouse buttons
        this.controls.mouseButtons = {
            LEFT: THREE.MOUSE.ROTATE,
            MIDDLE: THREE.MOUSE.DOLLY,
            RIGHT: THREE.MOUSE.ROTATE
        };
        
        // Set rotation limits
        this.controls.minPolarAngle = CONTROLS_CONFIG.MIN_POLAR_ANGLE;
        this.controls.maxPolarAngle = CONTROLS_CONFIG.MAX_POLAR_ANGLE;
        this.controls.minAzimuthAngle = -Infinity;
        this.controls.maxAzimuthAngle = Infinity;
        
        // Set zoom limits
        this.controls.minDistance = CONTROLS_CONFIG.MIN_DISTANCE;
        this.controls.maxDistance = CONTROLS_CONFIG.MAX_DISTANCE;
        
        // Invert rotation axes for better UX (like original code)
        this.controls.addEventListener('change', () => {
            if (this.controls.object) {
                this.controls.object.rotation.x = -this.controls.object.rotation.x;
                this.controls.object.rotation.y = -this.controls.object.rotation.y;
            }
        });
        
        this.controls.update();
    }

    /**
     * Setup custom zoom controls with discrete levels
     */
    setupZoomControls() {
        this.renderer.domElement.addEventListener('wheel', (event) => {
            event.preventDefault();
            
            // Change zoom level based on scroll direction
            if (event.deltaY < 0) {
                // Zoom in (narrower FOV)
                this.currentZoomLevel = Math.min(ZOOM_LEVELS.length - 1, this.currentZoomLevel + 1);
            } else {
                // Zoom out (wider FOV)
                this.currentZoomLevel = Math.max(0, this.currentZoomLevel - 1);
            }
            
            // Apply new FOV
            this.camera.fov = ZOOM_LEVELS[this.currentZoomLevel];
            this.camera.updateProjectionMatrix();
            
            // Trigger callback
            if (this.onZoomChange) {
                this.onZoomChange(this.currentZoomLevel + 1, ZOOM_LEVELS.length);
            }
            
            console.log(`🔍 Zoom level: ${this.currentZoomLevel + 1}/${ZOOM_LEVELS.length} (FOV: ${this.camera.fov}°)`);
        });
    }

    /**
     * Setup mouse interaction feedback
     */
    setupMouseInteraction() {
        this.renderer.domElement.addEventListener('mousedown', () => {
            this.renderer.domElement.style.cursor = 'grabbing';
        });
        
        this.renderer.domElement.addEventListener('mouseup', () => {
            this.renderer.domElement.style.cursor = 'grab';
        });
        
        this.renderer.domElement.addEventListener('mouseleave', () => {
            this.renderer.domElement.style.cursor = 'grab';
        });
    }

    /**
     * Start the animation loop
     * @param {Function} onFrame - Callback called on each frame
     */
    startAnimationLoop(onFrame) {
        const animate = () => {
            this.animationId = requestAnimationFrame(animate);
            
            // Update controls
            if (this.controls && this.controls.enabled) {
                this.controls.update();
            }
            
            // Call custom frame callback
            if (onFrame) {
                onFrame();
            }
            
            // Render scene
            this.renderer.render(this.scene, this.camera);
        };
        
        animate();
    }

    /**
     * Stop the animation loop
     */
    stopAnimationLoop() {
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
    }

    /**
     * Handle window resize
     */
    handleResize() {
        this.camera.aspect = this.container.clientWidth / this.container.clientHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    }

    /**
     * Add object to scene
     * @param {THREE.Object3D} object - Object to add
     */
    addToScene(object) {
        this.scene.add(object);
    }

    /**
     * Remove object from scene
     * @param {THREE.Object3D} object - Object to remove
     */
    removeFromScene(object) {
        this.scene.remove(object);
    }

    /**
     * Get the renderer's DOM element
     * @returns {HTMLCanvasElement}
     */
    getCanvas() {
        return this.renderer.domElement;
    }

    /**
     * Set cursor style
     * @param {string} cursor - CSS cursor value
     */
    setCursor(cursor) {
        this.renderer.domElement.style.cursor = cursor;
    }

    /**
     * Reset camera position and rotation to initial state
     */
    resetCamera() {
        // Reset camera position to initial
        this.camera.position.set(
            CAMERA_CONFIG.POSITION.x,
            CAMERA_CONFIG.POSITION.y,
            CAMERA_CONFIG.POSITION.z
        );

        // Reset camera rotation
        this.camera.rotation.set(0, 0, 0);

        // Reset controls target and update
        this.controls.target.set(0, 0, 0);
        this.controls.reset();
        this.controls.update();
    }

    /**
     * Set camera rotation (azimuthal angle) for initial view
     * @param {number} angleRadians - Rotation angle in radians
     */
    setCameraRotation(angleRadians) {
        if (!this.controls) return;

        // Get current camera position
        const radius = this.camera.position.length();

        // Calculate new camera position based on angle
        // Y rotation (azimuthal angle) around the sphere
        const x = radius * Math.sin(angleRadians);
        const z = radius * Math.cos(angleRadians);

        this.camera.position.set(x, this.camera.position.y, z);
        this.camera.lookAt(0, 0, 0);
        this.controls.update();
    }

    /**
     * Smoothly animate camera to target rotation
     * @param {number} targetAngleRadians - Target rotation in radians
     * @param {number} duration - Animation duration in milliseconds
     * @returns {Promise<void>}
     */
    animateCameraToRotation(targetAngleRadians, duration = 800) {
        return new Promise((resolve) => {
            const startTime = performance.now();
            const startPos = {
                x: this.camera.position.x,
                y: this.camera.position.y,
                z: this.camera.position.z
            };

            // Calculate start angle from current position
            const startAngle = Math.atan2(startPos.x, startPos.z);
            const radius = Math.sqrt(startPos.x * startPos.x + startPos.z * startPos.z);

            const animate = () => {
                const elapsed = performance.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);

                // Smoothstep easing
                const smoothProgress = progress * progress * (3 - 2 * progress);

                // Interpolate angle
                const currentAngle = startAngle + (targetAngleRadians - startAngle) * smoothProgress;

                // Apply position
                this.camera.position.x = radius * Math.sin(currentAngle);
                this.camera.position.y = startPos.y;
                this.camera.position.z = radius * Math.cos(currentAngle);

                this.camera.lookAt(0, 0, 0);
                this.controls.update();

                if (progress < 1) {
                    requestAnimationFrame(animate);
                } else {
                    resolve();
                }
            };

            animate();
        });
    }

    /**
     * Enable/disable camera controls
     * @param {boolean} enabled - Whether controls should be enabled
     */
    setControlsEnabled(enabled) {
        if (this.controls) {
            this.controls.enabled = enabled;
        }
    }

    /**
     * Cleanup resources
     */
    dispose() {
        this.stopAnimationLoop();
        
        if (this.renderer) {
            this.renderer.dispose();
        }
        
        if (this.controls) {
            this.controls.dispose();
        }
    }
}

